import { useState, useEffect, useMemo } from "react"
import { Link } from "react-router-dom"
import { X, ChevronLeft, ChevronRight, ImageIcon, ArrowDown, Search, ArrowRight } from "lucide-react"

const agronomieContext = require.context("../assets/Agronomie", false, /\.(jpg|jpeg|png|webp)$/i)
const informatiqueContext = require.context("../assets/informatique", false, /\.(jpg|jpeg|png|webp)$/i)
const agroKeys = agronomieContext.keys()
const infoKeys = informatiqueContext.keys()
// Curated subset: indices 10+ so no overlap with faculty pages (they use 0–9)
const agronomieSubset = agroKeys.slice(10, 22).map((key) => ({ src: agronomieContext(key), title: "", category: "FSAV · Agronomie" }))
const informatiqueSubset = infoKeys.slice(10, 22).map((key) => ({ src: informatiqueContext(key), title: "", category: "Sciences & Technologies · Informatique" }))

const GALLERY_IMAGES = [
    { src: require("../assets/TopHeroImages/1.jpg"), title: "Campus UNG", category: "Campus" },
    { src: require("../assets/TopHeroImages/2.png"), title: "Vie universitaire", category: "Vie étudiante" },
    { src: require("../assets/TopHeroImages/3.jpg"), title: "Université de Ngozi", category: "Campus" },
    { src: require("../assets/CampusImages/campus2.png"), title: "Campus", category: "Campus" },
    { src: require("../assets/HomeImage/IMG_0810.jpg"), title: "Campus", category: "Campus" },
    { src: require("../assets/HomeImage/Computer.jpg"), title: "Équipements", category: "Vie étudiante" },
    { src: require("../assets/HomeImage/deplome.jpg"), title: "Diplômes", category: "Événements" },
    { src: require("../assets/HomeImage/sportImage.jpg"), title: "Sport", category: "Vie étudiante" },
    { src: require("../assets/team/recteur.jpg"), title: "Équipe dirigeante", category: "Équipe" },
    { src: require("../assets/team/v-recteur.jpg"), title: "Vice-recteur", category: "Équipe" },
    ...agronomieSubset,
    ...informatiqueSubset,
]

const ALL_CATEGORIES = "Toutes"
const AUTOPLAY_MS = 5000

export default function Galerie() {
    const [lightboxIndex, setLightboxIndex] = useState(null)
    const [sliderIndex, setSliderIndex] = useState(0)
    const [categoryFilter, setCategoryFilter] = useState(ALL_CATEGORIES)
    const [searchQuery, setSearchQuery] = useState("")

    const categories = useMemo(() => {
        const set = new Set(GALLERY_IMAGES.map((i) => i.category).filter(Boolean))
        return [ALL_CATEGORIES, ...Array.from(set).sort()]
    }, [])

    const filteredImages = useMemo(() => {
        return GALLERY_IMAGES.filter((img) => {
            const matchCategory = categoryFilter === ALL_CATEGORIES || img.category === categoryFilter
            const matchSearch = !searchQuery.trim() || (img.title && img.title.toLowerCase().includes(searchQuery.toLowerCase()))
            return matchCategory && matchSearch
        })
    }, [categoryFilter, searchQuery])

    useEffect(() => {
        if (filteredImages.length === 0) return
        setSliderIndex((i) => Math.min(i, filteredImages.length - 1))
    }, [filteredImages.length])

    const openLightbox = (index) => setLightboxIndex(index)
    const closeLightbox = () => setLightboxIndex(null)
    const goPrev = () => setLightboxIndex((i) => (i <= 0 ? filteredImages.length - 1 : i - 1))
    const goNext = () => setLightboxIndex((i) => (i >= filteredImages.length - 1 ? 0 : i + 1))

    const slidePrev = () => setSliderIndex((i) => (i <= 0 ? filteredImages.length - 1 : i - 1))
    const slideNext = () => setSliderIndex((i) => (i >= filteredImages.length - 1 ? 0 : i + 1))

    useEffect(() => {
        if (filteredImages.length <= 1) return
        const t = setInterval(() => {
            setSliderIndex((i) => (i >= filteredImages.length - 1 ? 0 : i + 1))
        }, AUTOPLAY_MS)
        return () => clearInterval(t)
    }, [filteredImages.length])

    useEffect(() => {
        if (lightboxIndex === null) return
        const handleKey = (e) => {
            if (e.key === "Escape") closeLightbox()
            if (e.key === "ArrowLeft") goPrev()
            if (e.key === "ArrowRight") goNext()
        }
        window.addEventListener("keydown", handleKey)
        return () => window.removeEventListener("keydown", handleKey)
    }, [lightboxIndex])

    const getSrc = (item) => (typeof item.src === "string" ? item.src : item.src?.default ?? item.src)

    const currentSlide = filteredImages[sliderIndex]

    // Side cards: top = index+1, bottom-left = index+2, bottom-right = index+3 (wrapped)
    const nextIdx = (i) => (i + 1) % filteredImages.length
    const nextIdx2 = (i) => (i + 2) % filteredImages.length
    const nextIdx3 = (i) => (i + 3) % filteredImages.length
    const sideTop = filteredImages.length > 0 ? filteredImages[nextIdx(sliderIndex)] : null
    const sideLeft = filteredImages.length > 1 ? filteredImages[nextIdx2(sliderIndex)] : null
    const sideRight = filteredImages.length > 2 ? filteredImages[nextIdx3(sliderIndex)] : null

    // "Plus de photos" layout: first 5 images — 1 large (left) + 2x2 grid (right)
    const morePictures = filteredImages.slice(0, 5)
    const moreFeatured = morePictures[0]
    const moreGrid = morePictures.slice(1, 5) // up to 4 for 2x2

    return (
        <div className="bg-background min-h-screen">
            {/* Hero */}
            <header className="relative min-h-[40vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary/70" />
                    <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,hsl(var(--accent)),transparent)]" />
                </div>
                <div className="container relative z-10 py-14">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-3 mb-4">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">
                                Découverte
                            </span>
                            <span className="text-[10px] text-primary-foreground/70 uppercase tracking-widest font-bold">
                                Galerie
                            </span>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-serif font-bold tracking-tight text-white leading-[1.05] mb-4">
                            <span className="block italic font-light text-accent/90">Galerie</span>
                            <span className="block text-white mt-1">Université de Ngozi</span>
                        </h1>
                        <p className="text-base text-primary-foreground/90 max-w-xl mb-6 leading-relaxed">
                            Découvrez en images le campus, la vie étudiante et les temps forts de l&apos;UNG.
                        </p>
                        <a
                            href="#galerie-content"
                            className="inline-flex items-center gap-2 text-primary-foreground/90 hover:text-white text-sm font-semibold transition-colors"
                        >
                            Voir les photos
                            <ArrowDown className="w-4 h-4 animate-bounce" />
                        </a>
                    </div>
                </div>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-px h-8 bg-white/30 rounded-full" />
            </header>

            <section id="galerie-content" className="py-10 md:py-14 scroll-mt-24">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <nav className="flex items-center gap-2 text-xs text-muted-foreground mb-6">
                        <Link to="/" className="hover:text-primary transition-colors">Accueil</Link>
                        <span>/</span>
                        <span className="text-foreground font-medium">Galerie</span>
                    </nav>

                    {/* Toolbar */}
                    <div className="mb-6 rounded-xl border border-border bg-card p-4 shadow-sm">
                        <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-primary/10">
                                    <ImageIcon className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <h2 className="text-base font-bold text-foreground">Photos</h2>
                                    <p className="text-xs text-muted-foreground">
                                        <span className="font-semibold text-foreground">{filteredImages.length}</span> image{filteredImages.length !== 1 ? "s" : ""}
                                        {categoryFilter !== ALL_CATEGORIES && ` · ${categoryFilter}`}
                                    </p>
                                </div>
                            </div>
                            <div className="relative flex-1 sm:max-w-[220px]">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder="Rechercher..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full h-10 pl-9 pr-4 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                />
                            </div>
                        </div>
                        {categories.length > 1 && (
                            <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-2">
                                {categories.map((cat) => (
                                    <button
                                        key={cat}
                                        type="button"
                                        onClick={() => { setCategoryFilter(cat); setSliderIndex(0) }}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all border ${
                                            categoryFilter === cat
                                                ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                                : "bg-secondary/50 text-muted-foreground border-border hover:bg-primary/10 hover:text-foreground hover:border-primary/30"
                                        }`}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {filteredImages.length > 0 ? (
                        <>
                            {/* Top band: main slider (left) + side cards (right) */}
                            <div className="grid lg:grid-cols-12 gap-4 md:gap-5 mb-8">
                                {/* Main featured slider — ~65% */}
                                <div className="lg:col-span-8 relative">
                                    <div className="relative rounded-2xl border border-border bg-card shadow-lg overflow-hidden aspect-[16/10] md:aspect-[4/3]">
                                        <button
                                            type="button"
                                            onClick={() => openLightbox(sliderIndex)}
                                            className="absolute inset-0 w-full h-full block focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/30 rounded-2xl"
                                            aria-label="Agrandir l'image"
                                        >
                                            <img
                                                src={getSrc(currentSlide)}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); slidePrev() }}
                                            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-xl bg-primary/90 text-primary-foreground hover:bg-primary transition-colors z-10 shadow-lg"
                                            aria-label="Image précédente"
                                        >
                                            <ChevronLeft className="w-5 h-5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); slideNext() }}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-xl bg-primary/90 text-primary-foreground hover:bg-primary transition-colors z-10 shadow-lg"
                                            aria-label="Image suivante"
                                        >
                                            <ChevronRight className="w-5 h-5" />
                                        </button>
                                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                                            {filteredImages.map((_, idx) => (
                                                <button
                                                    key={idx}
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); setSliderIndex(idx) }}
                                                    className={`h-1.5 rounded-full transition-all ${
                                                        idx === sliderIndex ? "w-6 bg-accent" : "w-1.5 bg-white/60 hover:bg-white/90"
                                                    }`}
                                                    aria-label={`Image ${idx + 1}`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Side cards — ~35% */}
                                <div className="lg:col-span-4 flex flex-col gap-4">
                                    {sideTop && (
                                        <button
                                            type="button"
                                            onClick={() => { setSliderIndex(nextIdx(sliderIndex)); openLightbox(nextIdx(sliderIndex)) }}
                                            className="relative rounded-2xl border border-border bg-card overflow-hidden aspect-[4/3] shadow-sm hover:shadow-md hover:border-primary/30 transition-all text-left flex-1 min-h-0"
                                        >
                                            <img src={getSrc(sideTop)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                                        </button>
                                    )}
                                    <div className="grid grid-cols-2 gap-4 flex-shrink-0">
                                        {sideLeft && (
                                            <button
                                                type="button"
                                                onClick={() => { setSliderIndex(nextIdx2(sliderIndex)); openLightbox(nextIdx2(sliderIndex)) }}
                                                className="relative rounded-xl border border-border bg-card overflow-hidden aspect-square shadow-sm hover:shadow-md hover:border-primary/30 transition-all text-left"
                                            >
                                                <img src={getSrc(sideLeft)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                                            </button>
                                        )}
                                        {sideRight && (
                                            <button
                                                type="button"
                                                onClick={() => { setSliderIndex(nextIdx3(sliderIndex)); openLightbox(nextIdx3(sliderIndex)) }}
                                                className="relative rounded-xl border border-border bg-card overflow-hidden aspect-square shadow-sm hover:shadow-md hover:border-primary/30 transition-all text-left"
                                            >
                                                <img src={getSrc(sideRight)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Plus de photos — large card (left) + 2x2 grid (right) */}
                            {morePictures.length > 0 && (
                                <div className="rounded-2xl border border-border bg-card p-4 md:p-5 shadow-sm">
                                    <h3 className="text-base font-bold text-foreground mb-4">Plus de photos</h3>
                                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-[320px]">
                                        {/* Large featured card — left */}
                                        {moreFeatured && (
                                            <div className="lg:col-span-5 h-full min-h-[240px] lg:min-h-0">
                                                <button
                                                    type="button"
                                                    onClick={() => { setSliderIndex(0); openLightbox(0) }}
                                                    className="relative w-full h-full rounded-2xl border border-border bg-card overflow-hidden shadow-sm hover:shadow-lg hover:border-primary/30 transition-all text-left group"
                                                >
                                                    <img
                                                        src={getSrc(moreFeatured)}
                                                        alt=""
                                                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                    />
                                                </button>
                                            </div>
                                        )}
                                        {/* 2x2 grid — right */}
                                        <div className="lg:col-span-7 grid grid-cols-2 grid-rows-2 gap-3 h-full min-h-[280px] lg:min-h-0">
                                            {moreGrid.map((item, idx) => {
                                                const globalIdx = idx + 1
                                                return (
                                                    <button
                                                        key={idx}
                                                        type="button"
                                                        onClick={() => { setSliderIndex(globalIdx); openLightbox(globalIdx) }}
                                                        className="relative h-full rounded-xl border border-border bg-card overflow-hidden shadow-sm hover:shadow-md hover:border-primary/30 transition-all text-left group"
                                                    >
                                                        <img
                                                            src={getSrc(item)}
                                                            alt=""
                                                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                        />
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-16 rounded-2xl border-2 border-dashed border-border bg-secondary/30">
                            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                <ImageIcon className="w-7 h-7 text-primary" />
                            </div>
                            <p className="text-base font-bold text-foreground mb-2">Aucune image ne correspond</p>
                            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                                Modifiez la recherche ou la catégorie.
                            </p>
                            <button
                                type="button"
                                onClick={() => { setSearchQuery(""); setCategoryFilter(ALL_CATEGORIES) }}
                                className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors shadow-sm"
                            >
                                Tout afficher
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* Lightbox */}
            {lightboxIndex !== null && filteredImages[lightboxIndex] && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4"
                    onClick={closeLightbox}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Image agrandie"
                >
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); goPrev() }}
                        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 p-2.5 md:p-3 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
                        aria-label="Image précédente"
                    >
                        <ChevronLeft className="w-7 h-7 md:w-8 md:h-8" />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); goNext() }}
                        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 p-2.5 md:p-3 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
                        aria-label="Image suivante"
                    >
                        <ChevronRight className="w-7 h-7 md:w-8 md:h-8" />
                    </button>
                    <button
                        type="button"
                        onClick={closeLightbox}
                        className="absolute top-4 right-4 p-2.5 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
                        aria-label="Fermer"
                    >
                        <X className="w-6 h-6" />
                    </button>
                    <div
                        className="relative max-w-5xl max-h-[85vh] w-full flex flex-col items-center justify-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={getSrc(filteredImages[lightboxIndex])}
                            alt=""
                            className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl"
                        />
                        <div className="mt-3 flex items-center justify-center text-sm text-white/60">
                            {lightboxIndex + 1} / {filteredImages.length}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
